/**
 * Billing formatters — the one place money, credit-usage ratios and plan/action
 * enum labels are turned into user-visible strings.
 *
 * Why this module exists (Stage 12 audit of `pages/billing/BillingPage.tsx`):
 *
 * 1. **Money was rounded.** Both the plan price and the credit-pack price were
 *    rendered as `` `$${(cents / 100).toFixed(0)}` `` — a $49.50 plan printed
 *    `$50`. A price is never approximated.
 * 2. **The usage meter and the number printed beside it disagreed.** The meter
 *    divided `used` by `included + purchased`, ignoring `extra_credits`, while
 *    "Total available" right above it included them. A user holding 5,000 extra
 *    credits saw "95% used" while flush.
 * 3. **Enums were title-cased with `charAt(0).toUpperCase()`**, which rendered
 *    the SMB plan as "Smb" and left `pay_as_you_go` as `pay_as_you_go`.
 *
 * Every helper here is pure and total: no throw, no `NaN`, no `Infinity`, no
 * division by zero. The page is allowed to hand them partial API payloads.
 */

/** en-US, matching `formatCurrency` in `lib/utils.ts`. */
const MONEY_LOCALE = 'en-US'

const num = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0

/**
 * Exact money from an integer cents amount. **Never rounds to the dollar** —
 * 4950 is `$49.50`, not `$50`.
 *
 * Whole amounts drop the `.00` (`4900` → `$49`) because the plan price is a
 * 42px hero where a permanent `.00` is noise; a fractional amount always shows
 * both decimals, which is the case the old `.toFixed(0)` destroyed. The rule is
 * "show every cent that exists, and no cent that doesn't", applied everywhere.
 *
 * `currency` defaults to `'USD'`: the billing API sends no currency field
 * today, and the argument exists so the call sites are ready the day it does.
 * (Deliberately not a hardcoded `$` — `Intl` owns symbol placement.)
 */
export function formatCents(cents: number, currency: string = 'USD'): string {
  const safe = Math.round(num(cents))
  const isWhole = safe % 100 === 0
  return new Intl.NumberFormat(MONEY_LOCALE, {
    style: 'currency',
    currency,
    minimumFractionDigits: isWhole ? 0 : 2,
    maximumFractionDigits: isWhole ? 0 : 2,
  }).format(safe / 100)
}

/**
 * Unit price of a credit pack, e.g. `$0.012/credit`. Three decimals because a
 * credit costs a fraction of a cent and two would collapse every pack to
 * `$0.01`. Returns an em dash rather than `$Infinity/credit` when the pack
 * reports zero credits.
 */
export function formatPerCreditRate(
  priceCents: number,
  credits: number,
  currency: string = 'USD',
): string {
  if (!Number.isFinite(priceCents) || !Number.isFinite(credits) || credits <= 0) return '—'
  const perCredit = priceCents / credits / 100
  const money = new Intl.NumberFormat(MONEY_LOCALE, {
    style: 'currency',
    currency,
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(perCredit)
  return `${money}/credit`
}

/**
 * The credit-balance shape these helpers read. Every field is optional: the
 * balance endpoint is allowed to omit `total_remaining` (older deployments) and
 * the page must not produce `NaN` when it does.
 */
export interface CreditGrantLike {
  used_credits?: number | null
  included_credits?: number | null
  purchased_credits?: number | null
  extra_credits?: number | null
  remaining_credits?: number | null
  total_remaining?: number | null
}

/**
 * Everything the user can still spend: the authoritative `total_remaining` when
 * the API sends it, otherwise `remaining_credits + extra_credits` reconstructed.
 */
export function totalRemainingCredits(balance: CreditGrantLike | null | undefined): number {
  if (!balance) return 0
  if (typeof balance.total_remaining === 'number' && Number.isFinite(balance.total_remaining)) {
    return balance.total_remaining
  }
  return num(balance.remaining_credits) + num(balance.extra_credits)
}

/**
 * Total credits granted for the window — the denominator of the usage meter.
 *
 * Two derivations exist and a consistent payload makes them equal:
 *   `used + total_remaining`  ·  `included + purchased + extra`
 *
 * The larger wins. The first anchors the meter to the exact figure the UI prints
 * as "Total available", so the two can never disagree again; the second is the
 * floor for a payload that omits a remaining field. Taking the max means a
 * missing field can only ever make the meter read *lower*, never invent usage
 * that isn't there — the failure mode that produced "95% used" on a flush
 * account.
 */
export function creditsGranted(balance: CreditGrantLike | null | undefined): number {
  if (!balance) return 0
  const fromRemaining = num(balance.used_credits) + totalRemainingCredits(balance)
  const fromParts =
    num(balance.included_credits) + num(balance.purchased_credits) + num(balance.extra_credits)
  return Math.max(fromRemaining, fromParts, 0)
}

/**
 * The true monthly credit grant for a plan: its per-seat rate times the
 * account's current active-seat count. Backend scales the real grant the
 * same way (EntitlementService.create_and_activate_paid) — this mirrors
 * that so the UI never shows the bare per-seat rate as if it were the
 * total for a multi-seat account.
 */
export function totalGrantedCredits(planIncludedCredits: number, seatCount: number): number {
  return planIncludedCredits * Math.max(seatCount, 1)
}

/**
 * Share of granted credits consumed, as a whole number clamped to 0–100.
 * A zero grant reports 0% rather than dividing by zero.
 */
export function creditUsagePercent(balance: CreditGrantLike | null | undefined): number {
  const granted = creditsGranted(balance)
  if (granted <= 0) return 0
  const pct = Math.round((num(balance?.used_credits) / granted) * 100)
  if (!Number.isFinite(pct)) return 0
  return Math.min(100, Math.max(0, pct))
}

/**
 * Acronyms that must not be sentence-cased. `smb` is why this exists: the
 * estimator rendered the recommended plan as "Smb".
 */
const ACRONYMS = new Set([
  'ai', 'api', 'crm', 'csv', 'id', 'llm', 'pdf', 'saas', 'seo', 'sla', 'sms', 'smb', 'url',
])

/**
 * Humanize a snake_case enum for display: `pay_as_you_go` → "Pay as you go",
 * `smb` → "SMB", `workflow_steps` → "Workflow steps".
 *
 * Sentence case, not Title Case, per the canon in `design-aesthetics.md` §60
 * ("`partially_sent` → Partially sent"). Call sites must therefore NOT also
 * carry Tailwind's `capitalize` — CSS would re-capitalize every word and undo
 * both the sentence casing and the acronym handling.
 */
export function titleizeKey(value: string | null | undefined): string {
  if (!value) return ''
  const words = String(value).replace(/[_-]+/g, ' ').trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return ''
  return words
    .map((word, i) => {
      const lower = word.toLowerCase()
      if (ACRONYMS.has(lower)) return lower.toUpperCase()
      return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower
    })
    .join(' ')
}

/**
 * Resolve a plan *enum* (`smb`) to the display name the API already ships
 * (`SMB`), by looking it up in the loaded plan catalog. Falls back to
 * `titleizeKey` only when no plan matches — an unknown enum still reads as
 * prose instead of as a database value.
 */
export function resolvePlanLabel(
  planName: string | null | undefined,
  plans: ReadonlyArray<{ name?: string | null; display_name?: string | null }> = [],
): string {
  if (!planName) return ''
  const key = planName.trim().toLowerCase()
  const match = plans.find((plan) => (plan?.name || '').trim().toLowerCase() === key)
  const label = match?.display_name?.trim()
  return label || titleizeKey(planName)
}


/**
 * Which Billing tabs a role may see.
 *
 * Two independent grants feed this page: `billing.manage_subscription`
 * (Owner "manage" / Admin "view") is the money surface — plan, payment method,
 * credit packs, estimator — while `credit_budgets.view_usage` is the reporting
 * surface — the account's credit spend and history, which Manager and Analyst
 * hold read-only. An Analyst therefore lands on Usage with no plan or payment
 * controls anywhere on the page.
 *
 * Empty array = no access to the page at all; the caller shows the
 * "billing is managed by your account owner" notice instead.
 */
export function visibleBillingTabs(
  canManageBilling: boolean,
  canViewUsage: boolean,
): Array<'overview' | 'usage' | 'transactions' | 'estimator'> {
  if (canManageBilling) return ['overview', 'usage', 'transactions', 'estimator']
  if (canViewUsage) return ['usage', 'transactions']
  return []
}

export type BillingTab = 'overview' | 'usage' | 'transactions' | 'estimator'

/**
 * The tab that should be active, given the one currently selected and the set
 * the role may see — or `null` when the selection is already fine and must be
 * left alone.
 *
 * Exists because the permission map is fetched asynchronously
 * (`authStore.fetchPermissions`, `permissions: {}` until it lands), so on a
 * fresh load of /billing every `usePermission` answers "not allowed" for the
 * first render or two. Deriving the opening tab straight from that in a
 * `useState` initialiser meant an Owner got sent to Usage and stayed there:
 * the initialiser runs once, and the permissions arriving a moment later never
 * revised it. Choosing the tab in an effect instead lets the answer change as
 * the map loads.
 *
 * `null` while `visible` is empty is the important case — permissions have not
 * arrived yet (or the role has no access at all, and the caller renders the
 * access notice rather than any tab). Either way there is nothing to correct
 * toward, so the default selection stands untouched until we actually know.
 *
 * A user can only ever click a tab that is visible to them, so "not visible"
 * is never a deliberate choice being overridden — it is a default that turned
 * out to be wrong for this role, and `visible[0]` is where they can look.
 */
export function resolveBillingTab(
  active: BillingTab,
  visible: readonly BillingTab[],
): BillingTab | null {
  if (visible.length === 0) return null
  if (visible.includes(active)) return null
  return visible[0]
}
